import { Accordion, AccordionDetails, AccordionSummary, Box } from "@mui/material";
import { useSignal } from "@preact/signals-react";
import { isEmpty, isNil } from "lodash-es";
import NGIcon from "../../components/NGIcon/NGIcon";
import NGLayoutItem from "../../generators/NGLayoutItem";
import NGLayout from "../../layouts/NGLayout";
import { publishUserAction } from "../../library/analytics";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { log } from "../../library/logger";
import { isLayout } from "../../library/metadataUtils";
import { IAccordionGroupProps } from "../../library/NGFieldExtensions";
import { getClassName, getsxObject, getTestId, partitionMUIClasses } from "../../library/utils";

const tag = "NGAccordionGroup";

export default function NGAccordionGroup({ config, context, layoutItem }: IAccordionGroupProps) {
  const local = setupLocalState(
    config,
    {
      Style: useSignal(config.Style ?? {}),
      AccordionContainerStyle: useSignal(config.AccordionContainerStyle ?? {}),
      Classes: useSignal(config.Classes ?? []),
      Visible: useSignal(config.Visible ?? true),
      HideExpandIcon: useSignal(config.HideExpandIcon ?? false),
      ExpandDisabled: useSignal(config.ExpandDisabled ?? false),
      Items: useSignal(config.Items ?? []),
      Expanded: useSignal(config.Expanded ?? false),
      Label: useSignal(config.Label ?? "Label"),
      LayoutItem: useSignal(layoutItem),
      Header: useSignal(config.Header ?? {}),
    },
    context
  );

  const handlers = setupHandlers(config, context);

  const handleAccordionChange = (event: React.SyntheticEvent, expanded: boolean) => {
    const [_, classes] = partitionMUIClasses(local.Classes.value);
    const action = publishUserAction(config, context, "accordion_group_change", null, {
      Label: local.Label.value,
      Expanded: local.Expanded.value,
      EventCategory: "engagement",
      ElementClass: classes.join(" "),
    });
    if (!local.ExpandDisabled.value) {
      local.Expanded.value = expanded;
      handlers["onChange"]?.(event, expanded, action);
    }
  };

  return (
    <Accordion
      data-testid={getTestId(config)}
      data-type={config.__typename}
      aria-label={"accordion"}
      className={getClassName(local.Classes)}
      sx={getsxObject(local.Style.value, {
        boxShadow: "none",
        background: "transparent",
        color: "inherit",
        borderRadius: "0px",
      })}
      elevation={config.Elevation ?? 0}
      square={config.Square ?? true}
      expanded={local.Expanded.value}
      onChange={handleAccordionChange}
      slotProps={{
        transition: {
          unmountOnExit: true,
          "data-testid": `${getTestId(config, "collapse-root")}`,
        },
      }}
    >
      <AccordionSummary
        data-testid={`accordion-group-${local.Label.value}`}
        aria-controls={`accordion-group-${local.Label.value}`}
        id={`panel-${config.Id}-header`}
        expandIcon={
          !local.HideExpandIcon.value ? <NGIcon config={{ IconName: "ExpandLess" }} context={context} /> : undefined
        }
        sx={{
          padding: 0,
          ...(local.ExpandDisabled.value ? { cursor: "default !important" } : {}),
        }}
      >
        {!isEmpty(local.Header.value) ? (
          <NGLayoutItem config={local.Header.value} context={context} />
        ) : (
          local.Label.value
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: 0,
          ...getsxObject(local.AccordionContainerStyle.value),
        }}
      >
        {local.Items.value?.map((item, itemKey) => {
          if (isNil(item)) {
            log.error(tag, `Item with id '${item}' could not be found`);
            return <></>;
          }
          return (
            <Box key={`${local.Label.value}-${itemKey}`}>
              {!isLayout(item) ? (
                <NGLayoutItem config={item} context={context} />
              ) : (
                <NGLayout layoutItem={local.LayoutItem.value} layout={item} inDesignMode={false} context={context} />
              )}
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}
